
import React from 'react';
import MainNav from '@/components/MainNav';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Code2, Settings2, LayoutGrid } from 'lucide-react';
import AccessibilityWidget from '@/components/AccessibilityWidget';
import Footer from '@/components/Footer';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <header>
        <MainNav currentPath="home" />
      </header>
      <main className="container py-8 md:py-12">
        <section className="flex flex-col items-center text-center space-y-4 py-8 md:py-12">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
            Make your website accessible <span className="text-primary">to everyone</span>
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-[750px]">
            Accesstopia makes it easy to add comprehensive accessibility features to any website.
            Just add a simple script and empower all your users.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <Button asChild size="lg">
              <Link to="/pricing">
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/installation">
                Documentation
              </Link>
            </Button>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Accessibility for Everyone
              </h2>
              <p className="text-muted-foreground mb-6">
                Our mission is to create digital spaces that are welcoming and usable for all, 
                regardless of ability, background, or device.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/lovable-uploads/ee9050e6-5169-41b4-8e81-cdd254b09eef.png"
                alt="Diverse group of people representing inclusive digital accessibility" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card rounded-lg p-6 shadow-sm text-center flex flex-col items-center">
              <div className="mb-4 flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
                <Code2 className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Easy Installation</h3>
              <p className="text-muted-foreground text-center">
                Add a single line of code to your website and instantly improve accessibility for all users.
              </p>
            </div>
            <div className="bg-card rounded-lg p-6 shadow-sm text-center flex flex-col items-center">
              <div className="mb-4 flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
                <Settings2 className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Voice Navigation</h3>
              <p className="text-muted-foreground text-center">
                Allow users to navigate your site using voice commands for a hands-free experience.
              </p>
            </div>
            <div className="bg-card rounded-lg p-6 shadow-sm text-center flex flex-col items-center">
              <div className="mb-4 flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
                <LayoutGrid className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Customizable</h3>
              <p className="text-muted-foreground text-center">
                Tailor the appearance and features to match your brand and meet specific accessibility needs.
              </p>
            </div>
          </div>
        </section>
      </main>
      <AccessibilityWidget />
      <Footer />
    </div>
  );
};

export default HomePage;
